import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthProvider';
import { ChatView, type RestoreInput } from './ChatView';
import {
  listConversations,
  fetchConversationEvents,
  deleteConversation,
  setConversationTitle,
  firstUserMessage,
  type Conversation,
} from '../lib/conversations';
import { listMyPages, listPinnedPages, unpinPage, deletePage, setPageDaily, renamePage, type PageSummary } from '../lib/pages';
import { PAGE_UUID_RE, pageRepairPrompt } from '../render/primitives';
import { SIGNAL_BUBBLES } from '../data/seed';
import { Login } from './Login';
import { Sidebar } from './Sidebar';
import { Profile } from './Profile';
import { ModalShell } from './Modal';
import { Roundel } from './brand';

/**
 * The conversation id encoded in the URL (`/c/:sessionId`), or null for the
 * fresh-chat root (`/`). This is the SPA's only route: it lets a reload,
 * back/forward, or a bookmarked link restore the open conversation instead of
 * dropping to the empty hero. Everything else (`/`, unknown paths) is a fresh
 * chat. Server-side, `/c/*` isn't an asset and isn't a Worker route, so the SPA
 * `not_found_handling` fallback serves index.html and the SPA takes over here.
 */
function sessionIdFromPath(): string | null {
  const m = window.location.pathname.match(/^\/c\/([^/]+)\/?$/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** The `/c/:sessionId` path for a session id (id encoded — it may carry `/` or other URL-special chars). */
function pathForSession(sessionId: string): string {
  return `/c/${encodeURIComponent(sessionId)}`;
}

export function App() {
  const { user, loading, signOut } = useAuth();

  const [sideOpen, setSideOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // The active conversation. `null` = a fresh chat. When set, ChatView remounts
  // (keyed by sessionId) and replays `events` so the thread reopens.
  const [active, setActive] = useState<RestoreInput | null>(null);
  // The session id ChatView is currently showing, reported once the server
  // assigns it. For a restored thread this equals `active.sessionId`; for a
  // fresh chat that became a saved conversation after its first turn, `active`
  // stays null but this holds the live session id — so deleting that
  // conversation still knows to clear the pane.
  const [liveSessionId, setLiveSessionId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  // True once the first conversations fetch has resolved — so the one-time
  // URL restore can wait for the list (to recover a conversation's title)
  // instead of racing an empty initial state.
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  // Discovery lists: the user's own pages ("My Pages") and pages they've pinned
  // ("Shared/Pinned"). Refreshed alongside conversations — a render_page turn can
  // add a page, so the list updates when a turn completes.
  const [myPages, setMyPages] = useState<PageSummary[]>([]);
  const [pinnedPages, setPinnedPages] = useState<PageSummary[]>([]);
  const [restoring, setRestoring] = useState(false);
  // Bumped on "New chat" so a fresh chat → fresh chat still remounts ChatView.
  // Without it, going new→new leaves `active` at null (no key change), so the
  // in-progress fresh chat wasn't cleared.
  const [newNonce, setNewNonce] = useState(0);
  // Live-query indicator for the sidebar: whether a turn is streaming and the
  // question that's running.
  const [busy, setBusy] = useState(false);
  const [runningPrompt, setRunningPrompt] = useState<string | null>(null);
  // The conversation awaiting delete confirmation (drives the confirm modal);
  // `null` = no modal open.
  // The conversation awaiting delete confirmation, WITH the pages that delete
  // would keep, captured when the modal opened. Frozen on purpose: the lists
  // refresh on window focus, so recomputing live could delete a set the user
  // never read. The checkbox lives inside the modal component, which mounts per
  // open, so a destructive choice cannot be inherited from last time.
  const [pendingDelete, setPendingDelete] = useState<{ c: Conversation; kept: PageSummary[] } | null>(null);
  // The page ("My Pages") awaiting delete confirmation; `null` = no modal.
  const [pendingPageDelete, setPendingPageDelete] = useState<PageSummary | null>(null);
  // The page awaiting rename (drives the rename modal); `null` = no modal.
  const [pendingRename, setPendingRename] = useState<PageSummary | null>(null);
  // A one-shot prompt to seed a fresh chat with (the artifact wrapper's "Edit
  // with AI" deep-link, `/?edit=<pageId>`). Cleared on any navigation so it
  // can't re-fire when ChatView remounts.
  const [editPrompt, setEditPrompt] = useState<string | null>(null);
  // Text to open a fresh chat's composer with, NOT auto-sent (unlike editPrompt):
  // the question carried over when a closed conversation is continued, so the
  // user reviews it and presses send. Cleared on any navigation.
  const [draftPrompt, setDraftPrompt] = useState<string | null>(null);
  // The conversation the user most recently chose to open. Restore fetches are
  // async, so a slower earlier fetch must not clobber a later selection — only
  // the fetch whose sessionId still matches this ref is applied.
  const pendingSessionRef = useRef<string | null>(null);
  // The URL restore runs exactly once per page load; capture the initial path's
  // session id before any pushState so a deep-linked reload restores it.
  const didInitRestore = useRef(false);
  const initSessionId = useRef<string | null>(sessionIdFromPath());

  // Conversations deleted in this session, held until the server's own list stops
  // returning them. A delete is optimistic while its request is in flight, and a
  // refetch dispatched BEFORE it (focus, or opening another conversation) can land
  // after and put the row back — which reads as the delete not having worked.
  const deletedIdsRef = useRef<Set<string>>(new Set());

  const refreshConversations = useCallback(() => {
    if (!user) return;
    void listConversations().then((list) => {
      const deleted = deletedIdsRef.current;
      // Once the server agrees a conversation is gone, stop filtering it: the
      // set would otherwise mask a session id the user could legitimately meet
      // again, and it has served its purpose.
      for (const id of [...deleted]) if (!list.some((c) => c.sessionId === id)) deleted.delete(id);
      setConversations(deleted.size === 0 ? list : list.filter((c) => !deleted.has(c.sessionId)));
      setConversationsLoaded(true);
    });
  }, [user]);

  const refreshPages = useCallback(() => {
    if (!user) return;
    void listMyPages().then(setMyPages);
    void listPinnedPages().then(setPinnedPages);
  }, [user]);

  // A turn can create a page (render_page) as well as title the conversation, so
  // refresh both discovery lists and the history when one finishes.
  const onTurnComplete = useCallback(() => {
    refreshConversations();
    refreshPages();
  }, [refreshConversations, refreshPages]);

  // Load the history + pages once the user is known (and clear on sign-out).
  useEffect(() => {
    if (!user) {
      setConversations([]);
      setMyPages([]);
      setPinnedPages([]);
      // A held row carries the previous identity's question, and this component
      // never unmounts (the signed-out branch is an early return below all the
      // hooks), so without this it renders in the next user's sidebar.
      setStranded([]);
      deletedIdsRef.current = new Set();
      return;
    }
    refreshConversations();
    refreshPages();
  }, [user, refreshConversations, refreshPages]);

  // The artifact wrapper's "Edit with AI" deep-links here as `/?edit=<pageId>`
  // (the standalone page has no chat to seed a turn in). Turn it into a one-shot
  // prompt for a fresh chat, then strip the param so a reload doesn't re-seed.
  useEffect(() => {
    const editId = new URLSearchParams(window.location.search).get('edit');
    if (!editId) return;
    window.history.replaceState({}, '', '/');
    // This prompt is AUTO-SENT, so the id has to be a page id and nothing else: a
    // crafted `?edit=` link would otherwise run its own text as a turn against the
    // recipient's agent, under their access. The wrapper only ever emits a UUID.
    if (!PAGE_UUID_RE.test(editId)) return;
    setEditPrompt(pageRepairPrompt(editId, `${window.location.origin}/artifact/${editId}`));
  }, []);

  // Keep the discovery lists current when the tab regains focus — a page may
  // have been created, renamed, or deleted (incl. by another creator, dropping
  // a pin) in another tab or since we last looked. History too: a conversation
  // is indexed and titled server-side, so one started in another tab, or one
  // whose turn finished here after this client stopped watching it, exists on
  // the server while this list does not have it.
  useEffect(() => {
    if (!user) return;
    const onFocus = () => {
      refreshPages();
      refreshConversations();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, refreshPages, refreshConversations]);

  // Switching conversations abandons whatever was on screen. A fresh chat left
  // mid-turn keeps running server-side and is titled there rather than by this
  // client, so its row exists but arrives in this list only on the next
  // completed turn or a reload — which reads as the chat having been lost.
  // Refetch on every switch so it is reachable in the sidebar immediately.
  useEffect(() => {
    if (active?.sessionId) refreshConversations();
  }, [active?.sessionId, refreshConversations]);

  // The refetch above cannot see a conversation the SERVER has not indexed yet:
  // the row is written when a turn completes, so leaving a fresh chat seconds
  // into its first turn means there is genuinely nothing to fetch. The client
  // does not need the server for this — it knows the session id and the question
  // — so the session just left is held locally and merged into the list until
  // the fetched one carries it. Without this, "no way back to it" is true for
  // exactly the window the complaint was about.
  const [stranded, setStranded] = useState<Conversation[]>([]);

  /**
   * Hold the chat being abandoned, so it stays in the sidebar until the server's
   * own list carries it.
   *
   * Called explicitly by each site that abandons a chat rather than inferred from
   * a `liveSessionId` transition: an effect watching that transition also fires
   * when a conversation is DELETED (the reset that follows clears the id), which
   * re-adds the row that was just removed — and the prune below cannot take it
   * back, because the server no longer lists it. Naming the departure at the call
   * site means a delete simply never strands.
   *
   * `prompt` is the question that was running in the chat being left, passed in
   * for the same reason: `runningPrompt` survives a restore, so reading it later
   * can label one conversation with another's question.
   */
  const strandChat = useCallback((sessionId: string | null, prompt: string | null) => {
    // Nothing to hold without a question: a session this client never sent a turn
    // on is either a stale `/c/:id` from a bookmark or a restore that found
    // nothing, and holding it mints a row that can never be opened or deleted.
    if (!sessionId || !prompt) return;
    setStranded((prev) => {
      if (prev.some((c) => c.sessionId === sessionId)) return prev;
      const now = new Date().toISOString();
      return [...prev, { sessionId, title: prompt, createdAt: now, updatedAt: now }];
    });
  }, []);

  // Drop a held row the moment the server's own list carries it, so the real
  // row (with its server-assigned title and timestamps) is the one shown.
  useEffect(() => {
    setStranded((prev) => {
      const next = prev.filter((h) => !conversations.some((c) => c.sessionId === h.sessionId));
      return next.length === prev.length ? prev : next;
    });
  }, [conversations]);

  // What the sidebar lists: the server's history, plus anything left on screen
  // that has not reached it yet.
  const shownConversations = useMemo(
    () => [...conversations, ...stranded.filter((h) => !conversations.some((c) => c.sessionId === h.sessionId))],
    [conversations, stranded],
  );

  // Reset the surface to a fresh, empty chat. Bumping newNonce forces ChatView
  // to remount even from new→new (and from a fresh-but-saved chat, where
  // `active` is already null), so the previous thread never lingers on screen.
  const resetChat = useCallback((pushUrl = true) => {
    pendingSessionRef.current = null;
    setRestoring(false); // abort any in-flight restore (its result is now stale)
    setActive(null);
    setLiveSessionId(null);
    setRunningPrompt(null);
    setNewNonce((n) => n + 1);
    setEditPrompt(null); // a fresh chat drops any pending edit-seed
    setDraftPrompt(null); // and any carried-over draft
    // Reflect "fresh chat" in the URL (skip when a popstate already moved us).
    if (pushUrl && window.location.pathname !== '/') window.history.pushState({}, '', '/');
  }, []);

  // Continue a closed conversation in a fresh chat, carrying its last question
  // into the composer. resetChat clears draftPrompt, so the set has to follow it;
  // both land in one batch, so the remounted ChatView sees the draft.
  const continueInNewChat = useCallback(
    (draft: string) => {
      // Hold the chat being left, as every other abandonment path does: its row
      // is not in `conversations` until the post-turn refresh lands, so without
      // this it is briefly in neither list and the sidebar shows nothing for it.
      strandChat(liveSessionId, runningPrompt);
      resetChat();
      setDraftPrompt(draft || null);
    },
    [resetChat, strandChat, liveSessionId, runningPrompt],
  );

  // Editing a page from the sidebar opens a fresh chat with a DRAFT, not a sent
  // turn: the agent needs the artifactId (updating in place is what keeps the URL,
  // the refresh schedule and other people's pins pointing at the same page), but
  // only the user knows what they want changed. `draft` is never auto-sent.
  const editPage = useCallback(
    (p: PageSummary) => {
      setSideOpen(false); // on mobile the drawer covers the chat it just opened
      continueInNewChat(`Update the page \u201C${p.title ?? 'Untitled'}\u201D (artifactId ${p.id}) so that `);
    },
    [continueInNewChat],
  );

  // Fetch a session's prior events and put it on screen. Shared by a sidebar
  // click, a back/forward pop, and the one-time URL restore — the URL update is
  // the caller's job (they differ: push vs pop vs none). A session is addressed
  // by its id; the agent may also require the signed-in user to own it, and a
  // session they do not own then replays as empty.
  const restoreSession = useCallback(
    (sessionId: string, title: string | null) => {
      setSideOpen(false);
      setProfileOpen(false);
      setRestoring(true);
      pendingSessionRef.current = sessionId;
      void fetchConversationEvents(sessionId).then((events) => {
        if (pendingSessionRef.current !== sessionId) return; // superseded
        setActive({ sessionId, events });
        setLiveSessionId(sessionId);
        setRestoring(false);
        if (title === null) {
          const q = firstUserMessage(events);
          if (q) void setConversationTitle(sessionId, q).then(refreshConversations);
        }
      });
    },
    [refreshConversations],
  );

  // One-time URL restore: if the page loaded at /c/:id, reopen that conversation
  // once the list has loaded (so we can recover its title). Runs
  // once per page load, never on later list refreshes.
  useEffect(() => {
    if (!user || didInitRestore.current || !conversationsLoaded) return;
    didInitRestore.current = true;
    const sid = initSessionId.current;
    if (!sid) return;
    // The fetched list, not the merged one: a held row exists only for a session
    // this client had on screen, and the deep-linked restore is what first puts
    // one there, so a held row can never match here.
    const conv = conversations.find((c) => c.sessionId === sid);
    restoreSession(sid, conv?.title ?? null);
  }, [user, conversationsLoaded, conversations, restoreSession]);

  // Keep the URL in step with a fresh chat that became a saved session after its
  // first turn (App.active stays null then). replaceState — not push — so a
  // reload restores it without adding a spurious history entry.
  useEffect(() => {
    if (liveSessionId && sessionIdFromPath() !== liveSessionId) {
      window.history.replaceState({}, '', pathForSession(liveSessionId));
    }
  }, [liveSessionId]);

  // Back/forward: re-sync the on-screen conversation FROM the URL (no push — the
  // browser already moved us). Unknown/`/` → fresh chat.
  useEffect(() => {
    const onPop = () => {
      const sid = sessionIdFromPath();
      if (sid) {
        const conv = shownConversations.find((c) => c.sessionId === sid);
        if (sid !== liveSessionId) strandChat(liveSessionId, runningPrompt);
        restoreSession(sid, conv?.title ?? null);
      } else {
        strandChat(liveSessionId, runningPrompt);
        resetChat(false);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [shownConversations, restoreSession, resetChat, strandChat, liveSessionId, runningPrompt]);

  function newChat() {
    strandChat(liveSessionId, runningPrompt);
    resetChat();
    setSideOpen(false);
    setProfileOpen(false);
  }
  function handleSignOut() {
    signOut();
    resetChat();
    setProfileOpen(false);
  }
  function pickHistory(c: Conversation) {
    setEditPrompt(null); // opening a thread drops any pending edit-seed
    // New history entry so Back returns to where they were.
    if (sessionIdFromPath() !== c.sessionId) window.history.pushState({}, '', pathForSession(c.sessionId));
    if (c.sessionId !== liveSessionId) strandChat(liveSessionId, runningPrompt);
    restoreSession(c.sessionId, c.title);
  }
  // A page opens as the standalone interactive view (the app-origin wrapper),
  // in a new tab so the chat stays put.
  function openPage(p: PageSummary) {
    window.open(`/artifact/${p.id}`, '_blank', 'noopener');
  }
  function unpin(p: PageSummary) {
    setPinnedPages((prev) => prev.filter((x) => x.id !== p.id));
    void unpinPage(p.id).then((ok) => {
      if (!ok) refreshPages();
    });
  }
  // Toggle nightly auto-refresh for one of the user's pages (daily ↔ manual),
  // optimistic with a re-sync on failure (e.g. a guest 403).
  function togglePageDaily(p: PageSummary) {
    const daily = p.refreshInterval !== 'daily';
    setMyPages((prev) => prev.map((x) => (x.id === p.id ? { ...x, refreshInterval: daily ? 'daily' : 'manual' } : x)));
    void setPageDaily(p.id, daily).then((ok) => {
      if (!ok) refreshPages();
    });
  }
  // Rename a page: optimistic across both lists, reverting the label on failure
  // (or adopting the server's redacted/capped title on success).
  function submitRename(p: PageSummary, title: string) {
    const next = title.trim();
    setPendingRename(null);
    if (!next || next === p.title) return;
    const apply = (t: string | null) => (x: PageSummary) => (x.id === p.id && t !== null ? { ...x, title: t } : x);
    setMyPages((prev) => prev.map(apply(next)));
    setPinnedPages((prev) => prev.map(apply(next)));
    void renamePage(p.id, next).then((stored) => {
      if (stored === null) {
        refreshPages(); // revert to server truth on failure
      } else {
        // Always re-assert the stored (possibly redacted) title — a focus
        // refresh may have overwritten the optimistic value with the old one.
        setMyPages((prev) => prev.map(apply(stored)));
        setPinnedPages((prev) => prev.map(apply(stored)));
      }
    });
  }
  // A page can be in My Pages AND pinned, so both lists have to drop it. Written
  // once because it was already expressed twice with different bodies, and the
  // copy that forgot `pinnedPages` left a deleted page clickable under Saved.
  const dropPagesLocally = useCallback((ids: string[]) => {
    const gone = new Set(ids);
    setMyPages((prev) => prev.filter((x) => !gone.has(x.id)));
    setPinnedPages((prev) => prev.filter((x) => !gone.has(x.id)));
  }, []);

  function confirmPageDelete() {
    const p = pendingPageDelete;
    if (!p) return;
    setPendingPageDelete(null);
    dropPagesLocally([p.id]);
    void deletePage(p.id).then((ok) => {
      if (!ok) refreshPages();
    });
  }
  // The pages this conversation published that a delete would KEEP. `promoted` is
  // server-computed because a client can't derive it: another user's pin isn't in
  // the owner's own listing. An older agent omits the field, and unknown must not
  // read as "will be deleted" — so an absent value keeps the page out of this list
  // and the modal simply says less rather than something untrue.
  const keptByDelete = (sessionId: string) =>
    // `sessionId &&` guards the shape, not a hypothetical: a page from an agent
    // before this contract has no sessionId, and `undefined === undefined` would
    // match every one of them — deleting pages from other conversations.
    myPages.filter((p) => Boolean(p.sessionId) && p.sessionId === sessionId && p.promoted === true);

  function confirmDelete(alsoDeletePages: boolean) {
    const pending = pendingDelete;
    if (!pending) return;
    const c = pending.c;
    // Opt-in: the owner may take the kept pages with the conversation, through
    // the per-page owner route rather than a flag on the delete, so the authority
    // that removes a page is the same one everywhere. The set is the one captured
    // when the modal opened, which is the set whose count the user read.
    if (alsoDeletePages && pending.kept.length > 0) {
      dropPagesLocally(pending.kept.map((p) => p.id));
      // Reconcile ONCE, after every delete settles. `ok` means the route accepted
      // the request, not that a row went — it answers ok for "matched nothing"
      // too — so the server's own list is the only thing that settles it. Per-
      // delete refreshes would also race each other back into the sidebar.
      void Promise.allSettled(pending.kept.map((p) => deletePage(p.id))).then(refreshPages);
    } else {
      // Even untouched, the cascade removed this conversation's UNPROMOTED pages
      // server-side, so the sidebar is stale until it re-reads.
      refreshPages();
    }
    setPendingDelete(null);
    // Drop it from the list immediately; if it was open, fall back to a fresh
    // chat. Re-sync from the server if the delete didn't actually take.
    deletedIdsRef.current.add(c.sessionId);
    setConversations((prev) => prev.filter((x) => x.sessionId !== c.sessionId));
    // A row still held locally (left mid-turn, not yet in the server's list) has
    // to go too, or the delete removes it from one list and the merge puts it
    // straight back.
    setStranded((prev) => prev.filter((x) => x.sessionId !== c.sessionId));
    // Clear the pane if the deleted conversation is the one on screen — opened
    // from history (active.sessionId) OR a fresh chat that became this session
    // after its first turn (liveSessionId, since `active` stays null then).
    if (c.sessionId === active?.sessionId || c.sessionId === liveSessionId) {
      resetChat();
    }
    void deleteConversation(c.sessionId).then((ok) => {
      if (!ok) refreshConversations();
    });
  }

  if (loading) {
    return (
      <div style={{ fontFamily: 'var(--font-sans)', height: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-2)' }}>
        <Roundel size={40} />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ fontFamily: 'var(--font-sans)' }}>
        <Login bubbles={SIGNAL_BUBBLES} />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'var(--font-sans)', display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--bg-2)' }}>
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Sidebar
          open={sideOpen}
          onClose={() => setSideOpen(false)}
          onNew={newChat}
          conversations={shownConversations}
          onPickHistory={pickHistory}
          onDelete={(c) => setPendingDelete({ c, kept: keptByDelete(c.sessionId) })}
          myPages={myPages}
          pinnedPages={pinnedPages}
          onOpenPage={openPage}
          onUnpin={unpin}
          onToggleDaily={togglePageDaily}
          onDeletePage={setPendingPageDelete}
          onRenamePage={setPendingRename}
          onEditPage={editPage}
          activeId={active?.sessionId ?? null}
          liveSessionId={liveSessionId}
          busy={busy}
          runningPrompt={runningPrompt}
          onOpenProfile={() => {
            setProfileOpen(true);
            setSideOpen(false);
          }}
        />
        {profileOpen && <Profile onClose={() => setProfileOpen(false)} onSignOut={handleSignOut} />}
        {pendingDelete && (
          <ModalShell onClose={() => setPendingDelete(null)} maxWidth={420}>
            <div style={{ padding: '26px 24px 22px' }}>
              <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 8 }}>Delete conversation</div>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--fg-2)' }}>
                Are you sure you want to delete this conversation? This can't be undone.
              </div>
              <DeleteKeptPages pages={pendingDelete.kept} onConfirm={confirmDelete} onCancel={() => setPendingDelete(null)} />
            </div>
          </ModalShell>
        )}
        {pendingPageDelete && (
          <ModalShell onClose={() => setPendingPageDelete(null)} maxWidth={420}>
            <div style={{ padding: '26px 24px 22px' }}>
              <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 8 }}>Delete page</div>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--fg-2)' }}>
                Delete <strong>{pendingPageDelete.title ?? 'this page'}</strong>? Its shareable link will stop working. This can't be undone.
              </div>
              <div style={{ display: 'flex', gap: 9, marginTop: 22 }}>
                <button
                  className="he-press"
                  onClick={() => setPendingPageDelete(null)}
                  style={{ flex: 1, padding: '11px 0', borderRadius: 11, border: '1px solid var(--border-1)', background: 'var(--bg-1)', color: 'var(--fg-1)', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  className="he-press"
                  onClick={confirmPageDelete}
                  style={{ flex: 1, padding: '11px 0', borderRadius: 11, border: 'none', background: '#c2403a', color: '#fff', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </ModalShell>
        )}
        {pendingRename && (
          <RenameModal page={pendingRename} onCancel={() => setPendingRename(null)} onSubmit={submitRename} />
        )}

        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* mobile-only menu strip (sidebar is always visible on desktop) */}
          <div className="he-mobilebar">
            <button
              className="he-press"
              onClick={() => setSideOpen(true)}
              aria-label="Menu"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M3 12h18M3 18h18" stroke="var(--fg-1)" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <Roundel size={26} />
          </div>

          {/* While fetching a conversation's prior events, show a brief loading
              affordance in the body instead of the (about-to-remount) chat. */}
          {restoring ? (
            <div style={{ flex: 1, display: 'grid', placeItems: 'center' }}>
              <Roundel size={36} />
            </div>
          ) : (
            <ChatView
              key={active?.sessionId ?? `new-${newNonce}`}
              restore={active ?? undefined}
              onSend={(prompt) => {
                setSideOpen(false);
                setRunningPrompt(prompt);
              }}
              onBusyChange={setBusy}
              onSessionId={setLiveSessionId}
              onTurnComplete={onTurnComplete}
              initialPrompt={editPrompt ?? undefined}
              draft={draftPrompt ?? undefined}
              onContinueInNewChat={continueInNewChat}
              /* Reporting opens a fresh chat holding a draft, which is what
                 continuing a closed thread already does. The reported
                 conversation stays in the sidebar. */
              onReport={continueInNewChat}
            />
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * What a conversation-delete will KEEP, and the option to take it as well.
 *
 * Renders nothing when there is nothing to keep, which is the common case — a
 * page only survives once someone gave it a life of its own (a daily schedule, a
 * public link, or another person saving it). The opt-in is the answer for someone
 * deleting a conversation because they want it gone rather than tidied: without
 * it the delete is silently partial, and neither intent is served.
 *
 * The warning under the checkbox is blunt on purpose: the reasons a page is kept
 * are all reasons someone else may be relying on it, and burying that would move
 * the silent third-party loss rather than fix it. It does not try to count who —
 * `pinnedAt` here is the viewer's OWN pin, and a public page's readers are not in
 * page_pins at all, so any number would understate the audience it claims to
 * measure.
 */
function DeleteKeptPages({
  pages,
  onConfirm,
  onCancel,
}: {
  pages: PageSummary[];
  onConfirm: (alsoDeletePages: boolean) => void;
  onCancel: () => void;
}) {
  // Local, so it mounts fresh with the modal: a destructive default cannot be
  // inherited from a previous open, because no state outlives one.
  const [alsoDelete, setAlsoDelete] = useState(false);
  const n = pages.length;
  return (
    <>
      {n > 0 && (
        <div style={{ marginTop: 14, padding: '11px 12px', borderRadius: 10, background: 'var(--bg-3)', fontSize: 13, lineHeight: 1.5, color: 'var(--fg-2)' }}>
          <div>
            {n === 1 ? 'This page will be kept' : `These ${n} pages will be kept`} in the sidebar, because
            {n === 1 ? ' it is ' : ' each one is '}scheduled, shared by link, or saved by someone.
          </div>
          {/* Named, not just counted: the lists refresh on window focus, so a
              number read a minute ago can describe a different set than the one
              the button acts on. A title the reader recognises cannot. */}
          <ul style={{ margin: '7px 0 0', paddingLeft: 18 }}>
            {pages.map((p) => (
              <li key={p.id} style={{ color: 'var(--fg-1)' }}>
                {p.title ?? 'Untitled page'}
              </li>
            ))}
          </ul>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 9, cursor: 'pointer' }}>
            <input type="checkbox" checked={alsoDelete} onChange={(e) => setAlsoDelete(e.target.checked)} style={{ marginTop: 2 }} />
            <span>
              Delete {n === 1 ? 'it' : 'them'} too
              {/* Conditional, because it is often untrue: a private page kept only
                  by its own schedule has no audience at all. */}
              <span style={{ color: 'var(--fg-3)' }}> — if anyone else saved {n === 1 ? 'it' : 'them'} or has the link, they lose access</span>
            </span>
          </label>
        </div>
      )}
      <div style={{ display: 'flex', gap: 9, marginTop: 22 }}>
        <button
          className="he-press"
          onClick={onCancel}
          style={{ flex: 1, padding: '11px 0', borderRadius: 11, border: '1px solid var(--border-1)', background: 'var(--bg-1)', color: 'var(--fg-1)', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}
        >
          Cancel
        </button>
        <button
          className="he-press"
          onClick={() => onConfirm(alsoDelete)}
          style={{ flex: 1, padding: '11px 0', borderRadius: 11, border: 'none', background: '#c2403a', color: '#fff', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}
        >
          Delete
        </button>
      </div>
    </>
  );
}

/** Modal for renaming a page (My Pages "Rename"). Local input state; Enter or
 *  Save submits, empty is blocked. The server trims/caps/redacts the result. */
function RenameModal({ page, onCancel, onSubmit }: { page: PageSummary; onCancel: () => void; onSubmit: (p: PageSummary, title: string) => void }) {
  const [value, setValue] = useState(page.title ?? '');
  const canSave = value.trim().length > 0;
  const submit = () => {
    if (canSave) onSubmit(page, value);
  };
  return (
    <ModalShell onClose={onCancel} maxWidth={420}>
      <div style={{ padding: '26px 24px 22px' }}>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 12 }}>Rename page</div>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          maxLength={120}
          placeholder="Page name"
          style={{ width: '100%', height: 40, borderRadius: 10, border: '1px solid var(--border-1)', background: 'var(--bg-1)', padding: '0 12px', fontSize: 14, fontFamily: 'inherit', color: 'var(--fg-1)', boxSizing: 'border-box', outline: 'none' }}
        />
        <div style={{ display: 'flex', gap: 9, marginTop: 22 }}>
          <button className="he-press" onClick={onCancel} style={{ flex: 1, padding: '11px 0', borderRadius: 11, border: '1px solid var(--border-1)', background: 'var(--bg-1)', color: 'var(--fg-1)', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}>
            Cancel
          </button>
          <button className="he-press" onClick={submit} disabled={!canSave} style={{ flex: 1, padding: '11px 0', borderRadius: 11, border: 'none', background: 'var(--he-purple)', color: '#fff', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.6 }}>
            Save
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
